import { useEffect, useMemo, useState } from 'react';

/**
 * Lightweight countdown helper to keep auction timers in sync.
 * It normalises the label and the visual state (running / warning / final / ended)
 * so multiple components can reuse the same formatting rules.
 *
 * @param {string | null | undefined} endsAt ISO string describing when the auction closes.
 */
export default function useAuctionCountdown(endsAt, options = {}) {
  const {
    serverTimeOffsetMs = 0,
    freeze = false,
  } = options;
  const parseTarget = useMemo(() => {
    if (!endsAt) return null;
    const parsed = new Date(endsAt);
    return Number.isNaN(parsed.getTime()) ? null : parsed;
  }, [endsAt]);

  const [now, setNow] = useState(() => Date.now() + serverTimeOffsetMs);

  useEffect(() => {
    setNow(Date.now() + serverTimeOffsetMs);
  }, [serverTimeOffsetMs]);

  useEffect(() => {
    if (!parseTarget || freeze) return undefined;

    // Tick every second so the UI feels responsive without being too noisy.
    const interval = setInterval(() => {
      setNow(Date.now() + serverTimeOffsetMs);
    }, 1000);

    return () => clearInterval(interval);
  }, [freeze, parseTarget, serverTimeOffsetMs]);

  const result = useMemo(() => {
    if (!parseTarget) {
      return {
        label: '',
        status: 'idle',
        remainingMs: null,
      };
    }

    const remainingMs = parseTarget.getTime() - now;
    if (remainingMs <= 0) {
      return {
        label: 'Auction closed',
        status: 'ended',
        remainingMs: 0,
      };
    }

    const totalSeconds = Math.floor(remainingMs / 1000);
    const days = Math.floor(totalSeconds / 86400);
    const hours = Math.floor((totalSeconds % 86400) / 3600);
    const minutes = Math.floor((totalSeconds % 3600) / 60);
    const seconds = totalSeconds % 60;

    // Use short, scannable labels that prioritise the most relevant units.
    let label;
    if (days > 0) {
      label = `${days}d ${hours}h left`;
    } else if (hours > 0) {
      label = `${hours}h ${minutes.toString().padStart(2, '0')}m left`;
    } else if (minutes > 0) {
      label = `${minutes}m ${seconds.toString().padStart(2, '0')}s left`;
    } else {
      label = `${seconds}s left`;
    }

    let status = 'running';
    if (remainingMs <= 5 * 60 * 1000) {
      status = 'final'; // final 5 minutes
    } else if (remainingMs <= 60 * 60 * 1000) {
      status = 'warning'; // last hour
    }

    return { label, status, remainingMs };
  }, [now, parseTarget]);

  return result;
}
